import { actionSide, isFuelPickAction } from './composition-side';
import { SystemTypeOption } from './recommendations-strip-logic';
import { picksFromCompatibilityCodes } from './system-composition-from-codes';
import {
  BlowerComponentId,
  CoilComponentId,
  CompositionIndex,
  CompositionPickAction,
  CompositionPicks,
  CompositionSide,
  FurnaceFuel,
  OutdoorComponentId,
  PackagedCoolingId,
  PackagedHeatingId
} from './system-composition-types';

// System-type composition machinery (ported from the
// system-selector-frontend ducted "System you want" stage): maps each
// catalog row to per-slot component picks, and resolves picks back to the
// single catalog system type they compose — the "80+ combinations become
// individual component choices" trick. Class membership is decided
// STRUCTURALLY from compatibilityCodes via systemTypeCodeClasses /
// systemTypeCodeFuel (same grammar as kept-system-type-annotation). The
// DB-authored label is never the index key; parseSystemTypeName is only
// the fallback for rows with no codes (the 16 unmapped single-component
// types). Pure functions of the catalog: no fetches, no state.

export const EMPTY_COMPOSITION_PICKS: CompositionPicks = {
  outdoor: null,
  blower: null,
  blowerFuel: null,
  coil: null,
  packagedCooling: null,
  packagedHeating: null,
  packagedFuel: null
};

const FUEL_BEARING_BLOWERS: ReadonlySet<BlowerComponentId> = new Set([
  'furnace',
  'mobile-home-furnace'
]);

export const isFuelBearingBlower = (
  blower: BlowerComponentId | null
): boolean => blower !== null && FUEL_BEARING_BLOWERS.has(blower);

const normalizeName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/next[\s-]?gen/g, 'next gen')
    .replace(/\s+/g, ' ')
    .trim();

type SplitSegment =
  | { slot: 'outdoor'; option: OutdoorComponentId }
  | { slot: 'blower'; option: BlowerComponentId; fuel: FurnaceFuel | null }
  | { slot: 'coil'; option: CoilComponentId };

const SPLIT_SEGMENTS: Record<string, SplitSegment | undefined> = {
  'air conditioner': { slot: 'outdoor', option: 'ac' },
  'heat pump': { slot: 'outdoor', option: 'hp' },
  'next gen air conditioner': { slot: 'outdoor', option: 'next-gen-ac' },
  'next gen heat pump': { slot: 'outdoor', option: 'next-gen-hp' },
  'ng furnace': { slot: 'blower', option: 'furnace', fuel: 'ng' },
  'lp furnace': { slot: 'blower', option: 'furnace', fuel: 'lp' },
  'oil furnace': { slot: 'blower', option: 'furnace', fuel: 'oil' },
  'ng mobile home furnace': {
    slot: 'blower',
    option: 'mobile-home-furnace',
    fuel: 'ng'
  },
  'lp mobile home furnace': {
    slot: 'blower',
    option: 'mobile-home-furnace',
    fuel: 'lp'
  },
  'oil mobile home furnace': {
    slot: 'blower',
    option: 'mobile-home-furnace',
    fuel: 'oil'
  },
  'electric mobile home furnace': {
    slot: 'blower',
    option: 'mobile-home-furnace',
    fuel: 'electric'
  },
  'air handler': { slot: 'blower', option: 'air-handler', fuel: null },
  'next gen air handler': {
    slot: 'blower',
    option: 'next-gen-air-handler',
    fuel: null
  },
  'modular blower': { slot: 'blower', option: 'modular-blower', fuel: null },
  'evaporator coil': { slot: 'coil', option: 'coil' },
  'next gen evaporator coil': { slot: 'coil', option: 'next-gen-coil' }
};

type PackagedName = {
  cooling: PackagedCoolingId;
  heating: PackagedHeatingId | null;
  fuel: FurnaceFuel | null;
};

const PACKAGED_NAMES: Record<string, PackagedName | undefined> = {
  'packaged air conditioner': {
    cooling: 'packaged-ac',
    heating: null,
    fuel: null
  },
  'packaged heat pump': { cooling: 'packaged-hp', heating: null, fuel: null },
  'packaged air conditioner / ng furnace': {
    cooling: 'packaged-ac',
    heating: 'packaged-furnace',
    fuel: 'ng'
  },
  'packaged air conditioner / lp furnace': {
    cooling: 'packaged-ac',
    heating: 'packaged-furnace',
    fuel: 'lp'
  },
  'packaged heat pump / ng furnace': {
    cooling: 'packaged-hp',
    heating: 'packaged-furnace',
    fuel: 'ng'
  },
  'packaged heat pump / lp furnace': {
    cooling: 'packaged-hp',
    heating: 'packaged-furnace',
    fuel: 'lp'
  }
};

export const hasPackagedPicks = (picks: CompositionPicks): boolean =>
  picks.packagedCooling !== null || picks.packagedHeating !== null;

// The composition has no combustion side at all: an air handler or modular
// blower in the blower slot. Distinct from "no fuel picked yet" — an empty
// blower slot (or a packaged unit with no Furnace picked yet) leaves the
// question open, so this stays false.
export const compositionIsElectricOnly = (picks: CompositionPicks): boolean =>
  picks.blower !== null && !isFuelBearingBlower(picks.blower);

// Whether a fuel is a possible answer on the split side. Electric-only
// compositions still take Electric — that is the honest answer for an air
// handler, and it buckets under the same Electric fuel token the catalog
// files those system types under — but no combustion fuel.
export const splitAcceptsFuel = (
  picks: CompositionPicks,
  fuel: FurnaceFuel
): boolean => fuel === 'electric' || !compositionIsElectricOnly(picks);

export const compositionAwaitsFuel = (picks: CompositionPicks): boolean =>
  (isFuelBearingBlower(picks.blower) && picks.blowerFuel === null) ||
  (picks.packagedHeating !== null && picks.packagedFuel === null);

export const compositionFuel = (picks: CompositionPicks): FurnaceFuel | null =>
  picks.blowerFuel ?? picks.packagedFuel;

const withAcceptableSplitFuel = (picks: CompositionPicks): CompositionPicks =>
  picks.blowerFuel !== null && !splitAcceptsFuel(picks, picks.blowerFuel)
    ? { ...picks, blowerFuel: null }
    : picks;

export const parseSystemTypeName = (name: string): CompositionPicks | null => {
  const packagedName = PACKAGED_NAMES[normalizeName(name)];
  if (packagedName) {
    return {
      ...EMPTY_COMPOSITION_PICKS,
      packagedCooling: packagedName.cooling,
      packagedHeating: packagedName.heating,
      packagedFuel: packagedName.fuel
    };
  }
  const picks: CompositionPicks = { ...EMPTY_COMPOSITION_PICKS };
  for (const rawSegment of name.split(' + ')) {
    const segment = SPLIT_SEGMENTS[normalizeName(rawSegment)];
    if (!segment) return null;
    if (segment.slot === 'outdoor') {
      if (picks.outdoor !== null) return null;
      picks.outdoor = segment.option;
    } else if (segment.slot === 'blower') {
      if (picks.blower !== null) return null;
      picks.blower = segment.option;
      picks.blowerFuel = segment.fuel;
    } else {
      if (picks.coil !== null) return null;
      picks.coil = segment.option;
    }
  }
  return picks;
};

export const compositionKey = (picks: CompositionPicks): string =>
  hasPackagedPicks(picks)
    ? [
        'packaged:',
        picks.packagedCooling ?? '',
        picks.packagedHeating ?? '',
        picks.packagedHeating !== null ? picks.packagedFuel ?? '' : ''
      ].join('|')
    : [
        picks.outdoor ?? '',
        picks.blower ?? '',
        isFuelBearingBlower(picks.blower) ? picks.blowerFuel ?? '' : '',
        picks.coil ?? ''
      ].join('|');

export const buildCompositionIndex = (
  catalog: SystemTypeOption[]
): CompositionIndex => {
  const optionsByKey = new Map<string, SystemTypeOption>();
  const picksById = new Map<number, CompositionPicks>();
  catalog.forEach((option) => {
    const picks =
      picksFromCompatibilityCodes(option.compatibilityCodes ?? []) ??
      parseSystemTypeName(option.name);
    if (!picks) return;
    const key = compositionKey(picks);
    if (!optionsByKey.has(key)) {
      optionsByKey.set(key, option);
    }
    picksById.set(option.id, picks);
  });
  return { optionsByKey, picksById };
};

export const arePicksEmpty = (picks: CompositionPicks): boolean =>
  picks.outdoor === null &&
  picks.blower === null &&
  picks.coil === null &&
  !hasPackagedPicks(picks);

export const resolvePickedSystemType = (
  picks: CompositionPicks,
  index: CompositionIndex
): SystemTypeOption | null => {
  if (arePicksEmpty(picks)) return null;
  if (compositionAwaitsFuel(picks)) return null;
  return index.optionsByKey.get(compositionKey(picks)) ?? null;
};

const carriedFuel = (
  blower: BlowerComponentId | null,
  fuel: FurnaceFuel | null
): FurnaceFuel | null => {
  if (!isFuelBearingBlower(blower)) return null;
  if (fuel === 'electric' && blower !== 'mobile-home-furnace') return null;
  return fuel;
};

const carriedPackagedFuel = (fuel: FurnaceFuel | null): FurnaceFuel | null =>
  fuel === 'electric' ? null : fuel;

const toggled = <T>(current: T | null, option: T): T | null =>
  current === option ? null : option;

type PackagedSlots = Pick<
  CompositionPicks,
  'packagedCooling' | 'packagedHeating' | 'packagedFuel'
>;

const packagedOnly = (
  picks: CompositionPicks,
  overrides: Partial<PackagedSlots>
): CompositionPicks => ({
  ...EMPTY_COMPOSITION_PICKS,
  packagedCooling: picks.packagedCooling,
  packagedHeating: picks.packagedHeating,
  packagedFuel: picks.packagedFuel,
  ...overrides
});

export const applyCompositionPick = (
  picks: CompositionPicks,
  action: CompositionPickAction
): CompositionPicks => {
  if (action.slot === 'packaged-cooling') {
    return packagedOnly(picks, {
      packagedCooling: toggled(picks.packagedCooling, action.option)
    });
  }
  if (action.slot === 'packaged-heating') {
    const packagedHeating = toggled(picks.packagedHeating, action.option);
    return packagedOnly(picks, {
      packagedHeating,
      packagedFuel:
        packagedHeating === null
          ? picks.packagedFuel
          : carriedPackagedFuel(picks.packagedFuel)
    });
  }
  if (action.slot === 'packaged-fuel') {
    return packagedOnly(picks, {
      packagedFuel: toggled(picks.packagedFuel, action.fuel)
    });
  }
  const base: CompositionPicks = {
    ...picks,
    packagedCooling: null,
    packagedHeating: null,
    packagedFuel: null
  };
  if (action.slot === 'blower-fuel') {
    if (!splitAcceptsFuel(picks, action.fuel)) return picks;
    return { ...base, blowerFuel: toggled(picks.blowerFuel, action.fuel) };
  }
  if (action.slot === 'outdoor') {
    return { ...base, outdoor: toggled(picks.outdoor, action.option) };
  }
  if (action.slot === 'coil') {
    return { ...base, coil: toggled(picks.coil, action.option) };
  }
  const nextBlower = toggled(picks.blower, action.option);
  return withAcceptableSplitFuel({
    ...base,
    blower: nextBlower,
    blowerFuel: isFuelBearingBlower(nextBlower)
      ? carriedFuel(nextBlower, picks.blowerFuel)
      : picks.blowerFuel
  });
};

// A catalog type with no fuel-bearing component is an electric system: it
// answers an Electric pick, never a combustion one.
const fuelContained = (
  signatureFuel: FurnaceFuel | null,
  partialFuel: FurnaceFuel | null
): boolean =>
  partialFuel === null ||
  signatureFuel === partialFuel ||
  (partialFuel === 'electric' && signatureFuel === null);

const hasSplitPicks = (picks: CompositionPicks): boolean =>
  picks.outdoor !== null ||
  picks.blower !== null ||
  picks.coil !== null ||
  picks.blowerFuel !== null;

const compositionSide = (picks: CompositionPicks): CompositionSide | null => {
  if (hasPackagedPicks(picks) || picks.packagedFuel !== null) {
    return 'packaged';
  }
  return hasSplitPicks(picks) ? 'split' : null;
};

const compositionContains = (
  signature: CompositionPicks,
  partial: CompositionPicks
): boolean =>
  compositionSide(signature) === compositionSide(partial) &&
  (partial.outdoor === null || signature.outdoor === partial.outdoor) &&
  (partial.blower === null || signature.blower === partial.blower) &&
  fuelContained(signature.blowerFuel, partial.blowerFuel) &&
  (partial.coil === null || signature.coil === partial.coil) &&
  (partial.packagedCooling === null ||
    signature.packagedCooling === partial.packagedCooling) &&
  (partial.packagedHeating === null ||
    signature.packagedHeating === partial.packagedHeating) &&
  fuelContained(signature.packagedFuel, partial.packagedFuel);

// The picks a component pick is judged against: the applied picks, but with
// a fuel the pick would have to discard put back — a component that cannot
// burn the fuel already chosen on its own side reads as unreachable instead
// of silently clearing that fuel. Cross-column clears are the intended
// mutual exclusion and never count as a discard.
const reachabilityCandidate = (
  picks: CompositionPicks,
  action: CompositionPickAction
): CompositionPicks => {
  const applied = applyCompositionPick(picks, action);
  if (isFuelPickAction(action)) return applied;
  if (actionSide(action) === 'split') {
    return picks.blowerFuel !== null && applied.blowerFuel === null
      ? { ...applied, blowerFuel: picks.blowerFuel }
      : applied;
  }
  return picks.packagedFuel !== null && applied.packagedFuel === null
    ? { ...applied, packagedFuel: picks.packagedFuel }
    : applied;
};

export const isCompositionPickReachable = (
  picks: CompositionPicks,
  action: CompositionPickAction,
  index: CompositionIndex
): boolean => {
  const candidate = reachabilityCandidate(picks, action);
  if (arePicksEmpty(candidate) && compositionFuel(candidate) === null) {
    return true;
  }
  for (const signature of index.picksById.values()) {
    if (compositionContains(signature, candidate)) return true;
  }
  return false;
};

export const picksForSystemTypeId = (
  systemTypeId: number | undefined,
  index: CompositionIndex
): CompositionPicks =>
  (systemTypeId !== undefined
    ? index.picksById.get(systemTypeId)
    : undefined) ?? EMPTY_COMPOSITION_PICKS;

// Reflects the shared fuel filter back onto a restored composition that
// cannot encode a fuel of its own, so the pill reads as selected. Only a
// fuel the composition could actually answer with: an electric-only system
// re-lights Electric, but never a combustion fuel it cannot burn.
export const withFallbackFuel = (
  picks: CompositionPicks,
  fallbackFuel: FurnaceFuel | null
): CompositionPicks => {
  if (fallbackFuel === null) return picks;
  if (hasPackagedPicks(picks)) {
    return picks.packagedFuel !== null
      ? picks
      : { ...picks, packagedFuel: fallbackFuel };
  }
  return picks.blowerFuel !== null || !splitAcceptsFuel(picks, fallbackFuel)
    ? picks
    : { ...picks, blowerFuel: fallbackFuel };
};
